import React, { useState, useEffect } from 'react';
import { Key, Users, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../components/ui/use-toast';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPanel = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [twoFAEmail, setTwoFAEmail] = useState('');
  const [twoFAPhone, setTwoFAPhone] = useState('');
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFAMessage, setTwoFAMessage] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail || !newPassword) {
      toast({ title: 'Error', description: 'Please select a user and enter a new password', variant: 'destructive' });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/auth/reset-password`, {
        token,
        email: resetEmail,
        new_password: newPassword
      });
      setMessage({ type: 'success', text: `Password successfully reset for ${resetEmail}` });
      toast({ title: 'Success', description: `Password reset for ${resetEmail}` });
      setNewPassword('');
      setResetEmail('');
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Failed to reset password';
      setMessage({ type: 'error', text: errMsg });
      toast({ title: 'Error', description: errMsg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Admin Panel</h1>

      <div className="bg-white rounded-lg shadow p-6 mb-6 max-w-xl">
        <div className="flex items-center gap-3 mb-4">
          <Key size={20} className="text-orange-500" />
          <h2 className="text-lg font-semibold">Reset User Password</h2>
        </div>
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Select User</label>
            <select
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="w-full border rounded px-3 py-2"
              data-testid="reset-password-user-select"
            >
              <option value="">-- Select user --</option>
              {users.map((u, idx) => (
                <option key={idx} value={u.email}>
                  {`${u.name || u.email} (${u.role}) - ${u.email}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">New Password</label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 6 chars)"
              data-testid="reset-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="reset-password-submit"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
          {message && (
            <div className={`mt-3 p-3 rounded text-sm font-medium ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-testid="reset-password-message">
              {message.text}
            </div>
          )}
        </form>
      </div>

      {/* 2FA Management */}
      <div className="bg-white rounded-lg shadow p-6 mb-6 max-w-xl">
        <div className="flex items-center gap-3 mb-4">
          <Shield size={20} className="text-orange-500" />
          <h2 className="text-lg font-semibold">Two-Factor Authentication (SMS)</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Select User</label>
            <select
              value={twoFAEmail}
              onChange={(e) => {
                setTwoFAEmail(e.target.value);
                const u = users.find(u => u.email === e.target.value);
                setTwoFAEnabled(u?.two_fa_enabled || false);
                setTwoFAPhone(u?.phone || '');
                setTwoFAMessage(null);
              }}
              className="w-full border rounded px-3 py-2"
              data-testid="2fa-user-select"
            >
              <option value="">-- Select user --</option>
              {users.map((u, idx) => (
                <option key={idx} value={u.email}>
                  {`${u.name || u.email} (${u.role}) - ${u.email}${u.two_fa_enabled ? ' [2FA ON]' : ''}`}
                </option>
              ))}
            </select>
          </div>
          {twoFAEmail && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Phone Number (for SMS codes)</label>
                <Input
                  type="tel"
                  value={twoFAPhone}
                  onChange={(e) => setTwoFAPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                  data-testid="2fa-phone-input"
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={twoFAEnabled}
                    onChange={(e) => setTwoFAEnabled(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="2fa-toggle"
                  />
                  <span className="text-sm font-medium">Enable 2FA for this user</span>
                </label>
              </div>
              <Button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('mcaToken');
                    await axios.post(`${API_BASE}/auth/toggle-2fa`, {
                      token,
                      email: twoFAEmail,
                      enabled: twoFAEnabled,
                      phone: twoFAPhone
                    });
                    setTwoFAMessage({ type: 'success', text: `2FA ${twoFAEnabled ? 'enabled' : 'disabled'} for ${twoFAEmail}` });
                    loadUsers();
                  } catch (err) {
                    setTwoFAMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to update 2FA' });
                  }
                }}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="2fa-save"
              >
                Save 2FA Settings
              </Button>
              {twoFAMessage && (
                <div className={`p-3 rounded text-sm font-medium ${twoFAMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {twoFAMessage.text}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 max-w-xl">
        <div className="flex items-center gap-3 mb-4">
          <Users size={20} className="text-orange-500" />
          <h2 className="text-lg font-semibold">User Accounts</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-3">Name</th>
              <th className="text-left py-2 px-3">Email</th>
              <th className="text-left py-2 px-3">Role</th>
              <th className="text-left py-2 px-3">2FA</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, idx) => (
              <tr key={idx} className="border-b">
                <td className="py-2 px-3">{u.name || 'N/A'}</td>
                <td className="py-2 px-3">{u.email}</td>
                <td className="py-2 px-3">
                  <span className={`px-2 py-1 rounded text-xs ${
                    u.role === 'admin' ? 'bg-red-100 text-red-700' :
                    u.role === 'syndicator' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {u.role}
                  </span>
                </td>
                <td className="py-2 px-3">
                  {u.two_fa_enabled ? (
                    <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">ON</span>
                  ) : (
                    <span className="text-xs text-gray-400">Off</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPanel;
